/**
 * The English message catalogue (T076c, FR-035, constitution 1.2.0). Typed against the French
 * catalogue, so a text missing here is a build error; tests/unit/i18n.test.ts also checks every
 * `{placeholder}` matches. French stays the default language.
 *
 * Wine vocabulary is a first draft for the owner to review (PR description lists it).
 */
import type { MessageKey } from './fr';

export const en: Record<MessageKey, string> = {
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'home.reduce': 'Drink a bottle',
  'drink.search': 'Search for a wine',
  'drink.searchPlaceholder': 'Estate or plot',
  'drink.noMatch': 'No wine in stock matches.',
  'rating.up': 'Raise the rating',
  'rating.down': 'Lower the rating',
  'rating.noRating': 'No rating',
  'common.confirm': 'Confirm',
  'common.delete': 'Delete',
  'common.save': 'Save',
  'common.signOut': 'Sign out',
  'common.loading': 'Loading…',

  'signIn.welcome': 'Welcome to your personal wine cellar!',
  'signIn.emailLabel': 'Email',
  'signIn.emailPlaceholder': 'abc@mail.com',
  'signIn.submit': 'Sign in',
  'signIn.passwordLabel': 'Password',
  'signIn.showPassword': 'Show password',
  'signIn.hidePassword': 'Hide password',
  'signIn.forgot': 'Forgot your password?',
  'signIn.refused': 'Wrong email or password, or access not allowed.',
  'signIn.tooManyAttempts': 'Too many attempts. Wait a few minutes, then try again.',
  'signIn.network':
    'The sign-in service could not be reached. Check your connection and try again.',
  'newPassword.title': 'Choose your password',
  'newPassword.intro':
    'For your first sign-in, replace the temporary password you received by email.',
  'newPassword.label': 'New password',
  'newPassword.confirm': 'Confirm the password',
  'newPassword.mismatch': 'The two passwords are not the same.',
  'newPassword.invalid': 'This password is not accepted. Check the rules above.',
  'newPassword.submit': 'Save and sign in',
  'reset.title': 'Forgotten password',
  'reset.intro': 'Enter your email: you will receive a code to choose a new password.',
  'reset.send': 'Send the code',
  'reset.codeSent': 'If this address is allowed, a code has just been sent to it by email.',
  'reset.codeLabel': 'Code received by email',
  'reset.submit': 'Change the password',
  'reset.invalidCode': 'This code is wrong or has expired.',
  'reset.done': 'Password changed. You can sign in.',
  'reset.back': 'Back to sign in',

  'settings.cellarName': 'Cellar name',
  'error.cellarNameTooLong': 'The cellar name can be at most 60 characters.',
  'cellar.mine': 'My cellar',
  'cellar.of': '{name}’s cellar',
  'cellar.switchTitle': 'Switch cellar',
  'cellar.choose': 'Choose a cellar',
  'cellar.usersTitle': 'App users',
  'cellar.usersHint':
    'Who can sign in to the app. Each user has their own cellar: this list does not depend on the cellar shown.',
  'cellar.manageUsers': 'Manage users and their cellars',
  'cellar.settings': 'Settings',
  'nav.label': 'Main navigation',
  'nav.home': 'Home',
  'nav.stock': 'Stock',
  'nav.expiring': 'Drink soon',
  'expiring.none': 'No wine past its window or due within 24 months.',
  'expiring.within': 'Drink within 24 months',
  'expiring.past': 'Past their date',
  'home.drunk': 'Bottle recorded: {wine}.',
  'home.drunkMany': '{bottles} recorded: {wine}.',
  'home.past': 'Drink soon',
  'filters.due': 'Drink-by',
  'cellar.loadFailed':
    'This cellar could not be loaded. Check your connection and reload the page.',

  'drink.action': 'Drink a bottle',
  'drink.title': 'Bottle drunk',
  'drink.rating': 'Rating',
  'drink.notes': 'Comment',
  'drink.confirm': 'Confirm',
  'rating.outOfFive': '{rating}/5',
  'rating.none': 'No rating',
  'rating.trendUp': 'Rating up',
  'rating.trendDown': 'Rating down',
  'rating.trendSame': 'Rating unchanged',
  'tasting.outsideCellar': 'outside the cellar',
  'drink.bottles': 'Bottles drunk',
  'drink.bottlesLess': 'One bottle fewer',
  'drink.bottlesMore': 'One more bottle',

  'wine.legend': 'Wine details',
  'wine.domaine': 'Estate',
  'wine.cepage': 'Variety',
  'wine.chooseCepage': 'Choose…',
  'wine.parcelle': 'Plot',
  'wine.annee': 'Vintage',
  'wine.pays': 'Country',
  'wine.choosePays': 'Choose a country',
  'wine.region': 'Region',
  'wine.regionOther': 'Other region',
  'wine.regionMissing': 'To complete',
  'wine.chooseRegion': 'Choose a region',
  'wine.regionNeedsCountry': 'Choose a country first',
  'error.invalidRegion': 'Choose a region of this country.',
  'wine.typeDeVin': 'Type',
  'wine.chooseType': 'Choose a type',
  'wine.chooseAnnee': 'Choose a year',
  'wineType.rouge': 'Red',
  'wineType.blanc': 'White',
  'wineType.rose': 'Rosé',
  'wineType.vendanges_tardives': 'Late harvest',
  'stock.one': '{count} bottle',
  'stock.other': '{count} bottles',

  'drinkUntil.legend': 'Drink-by',
  'drinkUntil.none': 'None',

  'purchase.title': 'Add a purchase',
  'purchase.legend': 'Purchase details',
  'purchase.quantity': 'Quantity',
  'purchase.unitPrice': 'Price (€)',
  'purchase.date': 'Purchase date',
  'purchase.notes': 'Details',
  'purchase.submit': 'Save',
  'purchase.addToExisting': 'Add the purchase to this wine',
  'purchase.missingFields': 'Please fill in all the required fields.',

  'tasting.history': 'History',
  'tasting.noRating': 'No rating',
  'tasting.notes': 'Tasting notes',
  'tasting.date': 'Tasting date',

  'error.invalidQuantity': 'The quantity must be a whole number of at least 1.',
  'error.invalidPrice': 'The unit price must be a whole number of euros, zero or more.',
  'error.invalidDate': 'This date does not exist.',
  'error.invalidRating': 'The rating must be between 1 and 5, in half points.',
  'error.wineNotFound': 'This wine is no longer in your cellar.',
  'error.wineDuplicate': 'This wine is already in your cellar.',

  'edit.field': 'Edit: {field}',
  'edit.open': 'Edit',
  'edit.close': 'Close',
  'edit.none': '—',
  'purchase.history': 'Purchases',
  'purchase.of': 'Purchase of {date}',
  'photos.title': 'Label',
  'photos.front': 'Front',
  'photos.back': 'Back',
  'photos.takeFront': 'Photograph the front',
  'photos.takeBack': 'Photograph the back',
  'photos.removeFront': 'Delete the photo of the front',
  'photos.removeBack': 'Delete the photo of the back',
  'photos.removeConfirm': 'Delete this photo?',
  'photos.alt': 'Label of {wine} ({side})',
  'photos.none': 'No photo yet',
  'photos.loading': 'Loading…',
  'photos.saving': 'Saving…',
  'photos.failed': 'The photo could not be saved. Try again.',
  'purchase.tastedElsewhere':
    'No bottle bought: a wine tasted elsewhere. Its rating and comment go in its history.',
  'purchase.delete': 'Delete this purchase',
  'purchase.deleteWarning': 'This purchase will be deleted and the stock recalculated.',
  'tasting.delete': 'Delete this tasting',
  'tasting.deleteWarning':
    'This tasting will be deleted. If the bottle came from the cellar, it goes back into it.',
  'wine.delete': 'Delete this wine',
  'wine.deleteWarning':
    'This wine and all its purchases and tastings will be deleted. This is permanent in the app.',
  'delete.confirm': 'Delete permanently',
  'error.wineDuplicateNamed': 'This wine is already in your cellar: {wine}.',
  'error.invalidDomaine': 'The estate is required.',
  'error.invalidCepage': 'The variety is required.',
  'error.invalidAnnee': 'The vintage must be between 1800 and next year.',
  'error.invalidPays': 'Choose a country from the list.',
  'error.invalidTypeDeVin': 'Choose a wine type.',
  'error.invalidDrinkUntil': 'The drink-by date is not valid.',
  'error.invalidMonth': 'The month is not valid.',

  'map.link': 'Map',
  'map.title': 'By country',
  'map.label': 'World map: the countries of your wines are in colour',
  'map.hint': 'Tap a country, on the map or in the list, to see its wines.',
  'map.empty': 'No wine yet: the map will fill in with your purchases.',
  'map.purchased': 'Bought',
  'map.consumed': 'Finished',
  'map.average': 'Average rating',
  'map.noRating': 'no rating',
  'map.countrySummary': '{country}: {purchased}, {consumed}, rating {rating}',
  'map.bought.one': '{count} bought',
  'map.bought.other': '{count} bought',
  'map.drunk.one': '{count} finished',
  'map.drunk.other': '{count} finished',
  'map.wines': 'Wines',
  'map.filters': 'Map filters',
  'map.status': 'Status',
  'map.status.drunk': 'Finished',
  'map.status.notDrunk': 'In stock',

  'digest.subject.one': 'Your cellar: 1 wine to watch',
  'digest.subject.other': 'Your cellar: {count} wines to watch',
  'digest.greeting': 'Hello,',
  'digest.intro': 'These wines in your cellar have reached a step towards their drink-by date:',
  'digest.reminder.36': '3 years left',
  'digest.reminder.24': '2 years left',
  'digest.reminder.12': '1 year left',
  'digest.reminder.6': '6 months left',
  'digest.reminder.0': 'Drink-by date reached',
  'digest.open': 'Open your cellar: {url}',
  'digest.optOut': 'To stop receiving this email: {url}',
  'settings.digest': 'Monthly email',
  'settings.digestLabel': 'Receive each month the list of wines to drink soon',
  'settings.digestFailed': 'This choice could not be saved. Try again.',

  'camera.stepFront': 'Photo 1 of 2: the front of the bottle',
  'camera.stepBack': 'Photo 2 of 2: the back of the bottle',
  'camera.takeFront': 'Take the front',
  'camera.takeBack': 'Take the back',
  'camera.skipBack': 'No back',
  'camera.unavailable':
    'The camera can’t open in the app. Tap “Scan bottle” again to use the phone’s camera.',
  'scan.action': 'Scan bottle',
  'scan.reading': 'Reading the label…',
  'scan.frontTaken': 'Front of the bottle taken. Add the back?',
  'scan.frontPhoto': 'Photo of the front of the bottle',
  'scan.addBack': 'Photograph the back',
  'scan.frontOnly': 'Read this photo only',
  'scan.done': 'Label read.',
  'scan.failed':
    'The label could not be read. Try again with a sharper photo, or enter the wine by hand.',
  'scan.unavailable': 'Label reading is not available right now: enter the wine by hand.',
  'scan.matchesExisting':
    'This wine is already in your cellar ({wine}): the purchase will be added to it.',

  'rating.online': 'Get online rating',
  'rating.onlineSearching': 'Looking up ratings…',
  'rating.onlineA11y': 'Online rating: {rating}/5',
  'rating.cardA11y': 'Rating {rating}/5',
  'rating.onlineNotes': 'Notes',
  'rating.onlineNote': '{day}: {site} {score} / {scale}',
  'assist.quotaDay':
    'Gemini’s free allowance is used up for today: try again tomorrow (it resets around 9 am Paris time).',
  'assist.quotaMinute':
    'Too many requests to Gemini in one minute (free allowance): try again in a minute.',
  'assist.quota': 'Gemini’s free allowance is used up for now: try again later.',
  'rating.onlineNone': 'No online rating found for this wine.',
  'rating.onlineUnavailable': 'Rating search is not available right now.',
  'complete.action': 'Online search',
  'complete.searching': 'Searching online…',
  'complete.done': 'Completed online.',
  'complete.none': 'Nothing found online for this wine.',
  'complete.unavailable': 'Online search is not available right now.',
  'suggest.searchSuggestions': 'Related Google searches',

  'month.1': 'January',
  'month.2': 'February',
  'month.3': 'March',
  'month.4': 'April',
  'month.5': 'May',
  'month.6': 'June',
  'month.7': 'July',
  'month.8': 'August',
  'month.9': 'September',
  'month.10': 'October',
  'month.11': 'November',
  'month.12': 'December',

  'drinkBy.before': 'Drink before {date}',

  'filters.search': 'Search',
  'filters.searchPlaceholder': 'Estate, variety, tasting note…',
  'filters.show': 'Filters',
  'filters.showActive': 'Filters ({count})',
  'filters.hide': 'Hide filters',
  'filters.any': 'All',
  'filters.searchList': 'Search the list',
  'filters.add': 'Add “{value}”',
  'filters.rating': 'Rating',
  'filters.includeEmpty': 'Include finished wines',
  'filters.reset': 'Reset filters',
  'filters.count.one': '{count} wine',
  'filters.count.other': '{count} wines',
  'filters.empty': 'No wine matches these filters.',
  'filters.allFinished': 'Every bottle in this cellar has been drunk.',
  'filters.showFinished': 'Show finished wines',

  'inventory.metrics': 'Key figures',
  'inventory.metricBottles': 'Stock',
  'inventory.bottleUnit.one': 'bottle',
  'inventory.bottleUnit.other': 'bottles',
  'inventory.metricExpired': 'Drink soon',
  'inventory.metricNone': '—',
  'inventory.addPurchase': 'Add a bottle',
  'inventory.showSearch': 'Search',
  'inventory.empty': 'Your cellar is empty. Add your first purchase to start.',
  'inventory.emptyOther': 'This cellar is empty.',

  'deleteCellar.action': 'Delete this cellar',
  'deleteCellar.warning':
    'All the cellar’s wines, purchases and tastings will be erased. This action is irreversible. Are you really sure?',
  'deleteCellar.acknowledge': 'I understand this deletion is permanent.',
  'deleteCellar.confirm': 'Delete',
  'deleteCellar.done': 'The cellar has been emptied.',
  'deleteCellar.missing': 'This user has not signed in yet: their cellar does not exist yet.',
  'deleteCellar.failed': 'The cellar could not be deleted. Nothing was erased, try again.',

  'export.own': 'Export my cellar as CSV',
  'export.other': 'Export this cellar as CSV',
  'export.empty': 'The cellar is empty: the file only holds the column headings.',
  'export.failed': 'The export could not be made. Check your connection and try again.',

  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.languageFailed': 'Your language could not be saved. Try again.',
  'language.fr': 'Français',
  'language.en': 'English',

  'password.rules':
    'At least 16 characters, including 4 digits, 4 letters and 4 special characters (for example ! @ # - _).',
  'password.tooShort': 'At least 16 characters.',
  'password.notEnoughDigits': 'At least 4 digits.',
  'password.notEnoughLetters': 'At least 4 letters.',
  'password.notEnoughSpecial': 'At least 4 special characters (for example ! @ # - _).',

  'admin.limitReached': 'You have reached the maximum number of users allowed (4, besides you).',
  'admin.alreadyExists': 'This address is already allowed.',
  'admin.cannotDisableOwner': 'You cannot stop the owner’s access.',
  'admin.invalidEmail': 'This email address is not valid.',
  'admin.notFound': 'This user no longer exists. Reload the list.',
  'admin.forbidden': 'Only the owner can manage users.',
  'admin.unavailable': 'The service is unavailable for now. Try again later.',
  'admin.network': 'The service could not be reached. Check your connection and try again.',
  'admin.title': 'App users',
  'admin.intro': 'You can add up to 4 users, besides you. Each cellar has a single user.',
  'admin.addEmailLabel': 'User’s email address',
  'admin.clearEmail': 'Clear the email address',
  'admin.add': 'Add user',
  'admin.added':
    'Invitation sent to {email}. Amazon Web Services (AWS) will also send them a confirmation email with a link to activate their monthly email. This email may be filtered in spam folder.',
  'admin.invited': 'Invited',
  'admin.owner': 'Owner (you)',
  'admin.enabled': 'Access granted',
  'admin.disabled': 'Access removed',
  'admin.accessSwitch': 'App access for {email}',

  'notFound.title': 'Page not found',
  'notFound.body': 'This page does not exist or you do not have access to it.',

  'error.wineAlreadyExists': 'This wine is already in your cellar. Add the purchase to this wine?',
  'error.stockEmpty':
    'There are no bottles left in stock. Record a purchase first, or add a tasting outside the cellar.',
  'error.stockWouldBeNegative': 'This change would take the stock below zero.',
  'error.recordChangedElsewhere':
    'This record was changed in the meantime. Reload it before continuing.',
  'error.futureMonth': 'The chosen month is in the future.',
  'error.saveFailed': 'Could not save. The change was undone, try again.',
  'error.undoFailed': 'Could not undo, try again.',
  'error.rateFailed': 'Could not save the rating, try again.',
};
